import { useEntityType } from '@aibees/elephant/hooks';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useLookalikeSeeds } from '@aibees/elephant/components/company-similarity-search/hooks/use-lookalike-seeds';
import { useIsPeopleLookalikeEnabled } from '@aibees/elephant/hooks/people';
import { LookalikeButtonBase } from '@aibees/shared/components';
import { useAdvanceTableConfigContext } from '@aibees/shared/features/advance-table/context';
import { classnames } from '@aibees/shared/utils';
import { Button, ClickAwayListener, Stack, Tooltip } from '@mui/material';
import { memo, useMemo } from 'react';
import { RelevancySummary } from '../relevancy-summary';
import { LookalikeButtonIcon } from './components/lookalike-button-icon';
import { LookalikeButtonSidebar } from './components/lookalike-button-sidebar';
import { LookalikeButtonTooltipContent } from './components/lookalike-button-tooltip-content';
import { useLookalikeHandler } from './hooks/use-lookalike-handler';
import { useSelectedLookalikeKey } from './hooks/use-selected-lookalike-key';
import * as Styled from './styles';
import * as SX from './sx';
import type { LookalikeButtonProps as Props } from './types';

export const LookalikeButton = memo(
  ({
    className,
    currentItem,
    isSidebar,
    hideScore,
    disabled,
    fullWidth,
    showPopover,
  }: Props) => {
    const { isPeople } = useEntityType(currentItem);
    const { maxStickyRows } = useAdvanceTableConfigContext();
    const items = useLookalikeSeeds();

    const { lookalikeKey, selected } = useSelectedLookalikeKey(currentItem);
    const { handleClick, changeOpen, open } = useLookalikeHandler(currentItem);
    const isPeopleLookalikeEnabled = useIsPeopleLookalikeEnabled();

    const isPeopleLookalikeDisabled = useMemo(
      () => !isPeopleLookalikeEnabled && isPeople,
      [isPeopleLookalikeEnabled, isPeople]
    );

    const isButtonDisabled =
      !currentItem.canSimilaritySearch || isPeopleLookalikeDisabled;
    const showLookalikeLabel = selected || !currentItem.relevancy;

    if (!lookalikeKey) return null;

    return (
      <Styled.Wrapper
        className={classnames('LookalikeButton', className)}
        sx={SX.root}
        key={currentItem.id}
        // fullWidth={fullWidth}
      >
        {isSidebar || showPopover ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <LookalikeButtonSidebar
              currentItem={currentItem}
              lookalikeKey={lookalikeKey}
              maxStickyRows={maxStickyRows ?? 0}
              onAddToCurrentSearch={handleClick}
              selected={selected}
              hideScore={hideScore}
              disabled={isButtonDisabled || disabled}
            />
          </Stack>
        ) : (
          <>
            {!selected && !!currentItem.relevancy && (
              <RelevancySummary relevancy={currentItem.relevancy} />
            )}

            <ClickAwayListener onClickAway={() => changeOpen(false)}>
              <Tooltip
                title={
                  <LookalikeButtonTooltipContent
                    max={maxStickyRows ?? 0}
                    onClose={() => changeOpen(false)}
                  />
                }
                arrow
                placement="top"
                open={open.value}
                onClose={() => changeOpen(false)}
              >
                {showLookalikeLabel ? (
                  <Button
                    className={classnames({
                      selected: showLookalikeLabel,
                    })}
                    startIcon={<LookalikeButtonIcon selected={selected} />}
                    disabled={isButtonDisabled}
                    color={selected ? 'primary' : 'inherit'}
                    variant="outlined"
                    onClick={handleClick}
                    size="small"
                    fullWidth={fullWidth}
                  >
                    Lookalike
                  </Button>
                ) : (
                  <LookalikeButtonBase
                    limitReached={items.length === maxStickyRows}
                    errorMessage="You have reached the limit of 5 lookalikes, Remove to add new ones."
                    type="icon"
                    onClick={handleClick}
                  />
                )}
              </Tooltip>
            </ClickAwayListener>
          </>
        )}
      </Styled.Wrapper>
    );
  }
);

LookalikeButton.displayName = 'LookalikeButton';
